import { ValueLabel } from "@features/ax/shared/ValueLabel";

import { Badge } from "@shared/ui/badge";
import { Button } from "@shared/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { ChevronDown, ChevronUp, Printer } from "lucide-react";
import React, { useState } from "react";
import { usePaymentDetail } from "../../hooks";
import { PaymentReceipt } from "./PaymentReceipt";

const formatLinkedEntity = (linkedData: any): string => {
  if (!linkedData) return "";
  if (typeof linkedData === "object") {
    const identifier =
      linkedData.invoice_no ||
      linkedData.invoiceNo ||
      linkedData.request_no ||
      linkedData.requestNo ||
      linkedData.id ||
      linkedData.referenceNo ||
      linkedData.reference_no;

    return identifier || "";
  }
  return String(linkedData);
};

interface PaymentDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  payment: any;
}

export const PaymentDetailsModal: React.FC<PaymentDetailsModalProps> = ({
  isOpen,
  onClose,
  payment,
}) => {
  const [isCashDetailsExpanded, setIsCashDetailsExpanded] = useState(true);
  const [isReceiptModalOpen, setIsReceiptModalOpen] = useState(false);
  const { data: paymentData } = usePaymentDetail(payment?.id);

  // Payment details grid data
  const paymentDetailsFields = [
    {
      label: "Payment ID",
      value: paymentData?.transactionId,
    },
    {
      label: "Consumer Name",
      value:
        paymentData?.consumer?.firstName +
        " " +
        paymentData?.consumer?.lastName,
    },
    {
      label: "Account No",
      value: paymentData?.consumer?.consumerNo,
    },
    {
      label: "Payment Method",
      value: paymentData?.paymentMode ?? "NA",
    },
    {
      label: "Payment Channel",
      value: paymentData?.paymentChannel ?? "NA",
    },
    {
      label: "Payment Type",
      value: paymentData?.paymentPayType,
    },
    {
      label: "Payment linked to",
      value:
        formatLinkedEntity(paymentData?.paymentLinkedTo) ||
        formatLinkedEntity(paymentData?.linkedEntity) ||
        paymentData?.linkedTo ||
        paymentData?.linkedBillId ||
        paymentData?.paymentDetails?.linkedTo ||
        "NA",
    },
    {
      label: "Recorded By",
      value: paymentData?.recordedBy,
    },
    {
      label: "Recorded On",
      value: paymentData?.recordedOn,
      className: "col-span-2",
    },
  ];

  // Cash details grid data
  const cashDetailsFields = [
    {
      label: "Cash Received",
      value: paymentData?.paymentSummary?.cashReceived,
    },
    {
      label: "Payment Date",
      value: paymentData?.paymentDate,
    },
  ];

  // Summary data
  const summaryItems = [
    {
      label: "Total Payable Amount",
      value: paymentData?.paymentSummary?.totalPayableAmount,
    },
    {
      label: "Credit applied",
      value: paymentData?.paymentSummary?.creditApplied,
    },
    {
      label: "Late Fee",
      value: paymentData?.paymentSummary?.lateFee?.amount,
    },
    {
      label: "Net Amount Paid",
      value: paymentData?.paymentSummary?.netAmountPaid,
    },
    {
      label: "Cash Received",
      value: paymentData?.paymentSummary?.cashReceived,
    },
    {
      label: "Change Returned",
      value: paymentData?.paymentSummary?.changeReturned,
    },
  ];

  const handlePrintReceipt = () => {
    setIsReceiptModalOpen(true);
  };

  return (
    <>
      {/* Main Payment Details Modal */}
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <DialogHeader className="border-b pb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <DialogTitle className="text-xl font-semibold">
                  Payment Details
                </DialogTitle>
                <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
                  {paymentData?.paymentReceivedStatus}
                </Badge>
              </div>
            </div>
            <p className="text-sm text-gray-600 mt-2">
              Transaction details for Receipt #{paymentData?.transactionId}
            </p>
          </DialogHeader>

          {/* Content */}
          <div className="space-y-6 py-4">
            {/* Payment Details Grid */}
            <div className="grid grid-cols-2 gap-x-8 gap-y-6">
              {paymentDetailsFields.map((field, index) => (
                <ValueLabel key={index} {...field} />
              ))}
            </div>

            {/* Cash Details Section */}
            <div className="border rounded-lg">
              <button
                onClick={() => setIsCashDetailsExpanded(!isCashDetailsExpanded)}
                className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
              >
                <h3 className="font-semibold text-gray-900">Cash Details</h3>
                {isCashDetailsExpanded ? (
                  <ChevronUp className="w-5 h-5 text-gray-500" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-500" />
                )}
              </button>

              {isCashDetailsExpanded && (
                <div className="px-4 pb-4 grid grid-cols-2 gap-x-8 gap-y-4 border-t pt-4">
                  {cashDetailsFields.map((field, index) => (
                    <ValueLabel key={index} {...field} />
                  ))}
                </div>
              )}
            </div>

            {/* Payment Summary */}
            <div className="border-t pt-6">
              <h3 className="font-semibold text-gray-900 mb-4">
                Payment Summary
              </h3>
              <div className="space-y-3">
                {summaryItems.map((item, index) => (
                  <div
                    key={index}
                    className={`flex justify-between text-sm ${
                      item.isHighlight ? "pt-2 border-t" : ""
                    }`}
                  >
                    <span
                      className={
                        item.isHighlight
                          ? "font-semibold text-orange-700"
                          : "text-gray-600"
                      }
                    >
                      {item.label}
                    </span>
                    <span
                      className={
                        item.isHighlight
                          ? "font-bold text-orange-700"
                          : "font-medium text-gray-900"
                      }
                    >
                      {item.value}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="border-t pt-4 flex justify-between">
            <Button
              variant="outline"
              onClick={handlePrintReceipt}
              className="flex items-center gap-2"
            >
              <Printer className="w-4 h-4" />
              Print Receipt
            </Button>
            <Button onClick={onClose}>Close</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Receipt Handling Component */}
      <PaymentReceipt
        isOpen={isReceiptModalOpen}
        onClose={() => setIsReceiptModalOpen(false)}
        paymentId={payment?.id}
      />
    </>
  );
};
